import { Tabs, FloatingIndicator, Button, Loader } from '@mantine/core';
import { IconArrowLeft, IconPencil, IconQrcode, IconTrash, IconUser } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  resolveChecklistColor,
  resolveChecklistIcon,
} from 'src/Components/Checklists/checklistVisuals.helper';
import { ChecklistSubmissionDetails } from 'src/Components/Forms/ChecklistSubmission/ChecklistSubmissionDetails';
import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { RowActions } from 'src/Components/Shared/RowActions';
import { RelatedTasksList } from 'src/Components/Tasks/RelatedTasksList';
import { useCreateChecklistExport } from 'src/Hooks/Mutations/useCreateChecklistExport';
import { useDeleteChecklist } from 'src/Hooks/Mutations/useDeleteChecklist';
import useFetchChecklist from 'src/Hooks/Queries/useFetchChecklist';
import useFetchChecklistSubmissions from 'src/Hooks/Queries/useFetchChecklistSubmissions';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import useSortableRecords from 'src/Hooks/Shared/useSortableRecords';

import ChecklistSubmissionCreationFormWrapper from '../../Forms/ChecklistSubmission/ChecklistSubmissionCreationFormWrapper/ChecklistSubmissionCreationFormWrapper.component';
import { AnimatedRoutedDrawer } from '../AnimatedRoutedDrawer';

import styles from './ChecklistSubmissionsListDrawer.module.scss';

type ChecklistParams = {
  checklistId: string;
};

const ChecklistSubmissionsListDrawer = () => {
  const { checklistId } = useParams<ChecklistParams>();
  const { data: submissions, isLoading: submissionsLoading } =
    useFetchChecklistSubmissions(checklistId);
  const { data: checklist } = useFetchChecklist(checklistId);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { mutate: deleteChecklist } = useDeleteChecklist();

  const navigate = useNavigate();
  const location = useLocation();

  const handleEdit = () => {
    const currentPath = location.pathname;

    const newPath = currentPath.replace(/\/submissions$/, '/edit');

    navigate(newPath, { state: { disableAnimation: true }, replace: true });
  };

  const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
  const [value, setValue] = useState<string | null>('details');
  const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({});
  const setControlRef = (val: string) => (node: HTMLButtonElement) => {
    controlsRefs[val] = node;
    setControlsRefs(controlsRefs);
  };

  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [exportUrl, setExportUrl] = useState<string | undefined>(undefined);
  const [exportName, setExportName] = useState<string | undefined>(undefined);

  const { mutate: exportChecklist } = useCreateChecklistExport({
    onSuccess: data => {
      setExportUrl(data.publicUrl);
      setShowQRCodeModal(true);
    },
  });

  const [viewingSubmissionId, setViewingSubmissionId] = useState<string | undefined>(undefined);

  const assetAboveQuotas = useIsAssetAboveQuotas();
  const {
    sortedRecords: sortedSubmissions,
    sortStatus,
    setSortStatus,
  } = useSortableRecords(submissions);

  return (
    <AnimatedRoutedDrawer title={t('checklist.checklist', 'Checklist')}>
      {() => (
        <>
          <QRCodeModal
            open={showQRCodeModal}
            url={exportUrl ?? ''}
            onClose={() => setShowQRCodeModal(false)}
            exportName={exportName ?? ''}
          />
          <div className={styles.topLine}>
            <div className={styles.titleGroup}>
              {checklist &&
                (() => {
                  const Icon = resolveChecklistIcon(checklist.icon);
                  const color = resolveChecklistColor(checklist.color);
                  return (
                    <div className={styles.iconBadge} style={{ backgroundColor: `${color}1f` }}>
                      <Icon size={20} color={color} />
                    </div>
                  );
                })()}
              <div className={styles.title}>{checklist?.title}</div>
            </div>
            <RowActions
              actions={[
                {
                  key: 'edit',
                  label: t('checklist.table.menu.edit', 'Modifier'),
                  icon: <IconPencil size={18} />,
                  disabled: assetAboveQuotas,
                  onClick: handleEdit,
                },
                {
                  key: 'export',
                  label: t('checklist.table.menu.export', 'Exporter'),
                  icon: <IconQrcode size={18} />,
                  onClick: () => {
                    setExportName(checklist?.title);
                    exportChecklist([checklistId ?? '']);
                  },
                },
                {
                  key: 'delete',
                  label: t('checklist.table.menu.delete', 'Supprimer'),
                  icon: <IconTrash size={18} />,
                  variant: 'danger',
                  onClick: () => {
                    deleteChecklist(checklistId ?? '');
                    navigate(-1);
                  },
                },
              ]}
            />
          </div>

          {checklistId && (
            <div className={styles.relatedTasks}>
              <RelatedTasksList entityType="checklist" entityId={checklistId} />
            </div>
          )}

          <Tabs className={styles.tabs} value={value} onChange={setValue}>
            <Tabs.List ref={setRootRef} className={styles.tabList}>
              <Tabs.Tab value="details" ref={setControlRef('details')}>
                {t('checklist.tabs.details', 'Détails')}
              </Tabs.Tab>
              <Tabs.Tab value="log" ref={setControlRef('log')}>
                {t('checklist.tabs.log', 'Journal')}
              </Tabs.Tab>

              <FloatingIndicator
                target={value ? controlsRefs[value] : null}
                parent={rootRef}
                className={styles.indicator}
              />
            </Tabs.List>
            <div className={styles.content}>
              <Tabs.Panel value="details">
                <div className={styles.fullFormContainer}>
                  {checklistId ? (
                    <ChecklistSubmissionCreationFormWrapper
                      checklistId={checklistId}
                      onCreate={() => {}}
                    />
                  ) : (
                    <Loader />
                  )}
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="log">
                {viewingSubmissionId ? (
                  <>
                    <Button
                      variant="transparent"
                      leftSection={<IconArrowLeft size={22} />}
                      onClick={() => setViewingSubmissionId(undefined)}
                    >
                      {t('checklist.submissions.goBack', 'Revenir en arrière')}
                    </Button>
                    <ChecklistSubmissionDetails checklistSubmissionId={viewingSubmissionId} />
                  </>
                ) : (
                  <DataTable
                    withRowBorders={false}
                    style={{ width: '100%', minHeight: '10rem' }}
                    records={sortedSubmissions}
                    sortStatus={sortStatus}
                    onSortStatusChange={setSortStatus}
                    noRecordsText={t(
                      'checklist.submissions.empty',
                      'Aucune soumission enregistrée'
                    )}
                    fetching={submissionsLoading}
                    rowStyle={() => ({ height: 54 })}
                    onRowClick={({ record }) => setViewingSubmissionId(record.id)}
                    columns={[
                      {
                        accessor: 'submitterFirstName',
                        title: t('checklist.submissions.submitterName', 'Nom'),
                        sortable: true,
                        render: value => (
                          <div className={styles.name}>
                            <div className={styles.userRow}>
                              {value.submitterFirstName || value.submitterLastName ? (
                                <>
                                  <IconUser size={16} />
                                  {[value.submitterFirstName, value.submitterLastName].join(' ')}
                                </>
                              ) : (
                                <>
                                  <IconQrcode size={16} />
                                  {t('checklist.invitedUser', 'Invité')}
                                </>
                              )}
                            </div>
                          </div>
                        ),
                      },
                      {
                        accessor: 'checkedItemsCount',
                        title: t('checklist.submissions.checkedItemsCount', 'Cases cochées'),
                        sortable: true,
                        render: value => (
                          <div
                            className={styles.count}
                          >{`${value.checkedItemsCount}/${value.totalItemsCount}`}</div>
                        ),
                        hidden: isMobile,
                      },
                      {
                        accessor: 'submittedAt',
                        title: t('checklist.submissions.submittedAt', 'Date'),
                        sortable: true,
                        render: value => (
                          <div
                            className={styles.date}
                          >{`${value.submittedAt.format('L')} ${value.submittedAt.format('LT')}`}</div>
                        ),
                      },
                    ]}
                  />
                )}
              </Tabs.Panel>
            </div>
          </Tabs>
        </>
      )}
    </AnimatedRoutedDrawer>
  );
};

export default ChecklistSubmissionsListDrawer;
